import { VipContext, type VipTier } from './components/VipContext'
import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from './api'
import { saveLocationLabel } from './storage'
import { PlayerAccessContext, PlayerAccessDialog, usePlayerIdentity } from './components/PlayerAccess'
import { PhoneLoginDialog } from './components/PhoneLoginDialog'
import { AppShell } from './components/AppShell'
import type { Panel, Preferences } from './view-types'
import { useAuthSession } from './hooks/useAuthSession'
import { useCloudSave } from './hooks/useCloudSave'
import { useLocalGame } from './hooks/useLocalGame'
import { useViewportHeight } from './hooks/useViewportHeight'
import { continueResult, type GameState } from './game'
import { playGameSound, setGameAudioEnabled, startGameBgm, stopGameBgm, unlockGameAudio } from './animation/gameAudio'

const PREF_KEY = 'sun-ge-react-preferences-v1'
const defaultPreferences: Preferences = { autoContinue: false, autoDelay: 8000, reduceMotion: false, soundEnabled: true }

function readPreferences(): Preferences {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PREF_KEY) || 'null') as Partial<Preferences> | null
    const legacySoundEnabled = window.localStorage.getItem('sun_snd') !== '0'
    return {
      autoContinue: parsed?.autoContinue === true,
      autoDelay: [5000, 8000, 12000].includes(Number(parsed?.autoDelay)) ? Number(parsed?.autoDelay) : 8000,
      reduceMotion: parsed?.reduceMotion === true,
      soundEnabled: typeof parsed?.soundEnabled === 'boolean' ? parsed.soundEnabled : legacySoundEnabled
    }
  } catch {
    return defaultPreferences
  }
}

function App() {
  const [preferences, setPreferences] = useState<Preferences>(() => readPreferences())
  const [vipInitialTier, setVipInitialTier] = useState<VipTier | null>(null)
  const [paymentReturnOrderId, setPaymentReturnOrderId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('payment_order'))
  const [systemReduceMotion, setSystemReduceMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setSystemReduceMotion(media.matches)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  const [visible, setVisible] = useState(true)
  const [openAccountAfterAuthError, setOpenAccountAfterAuthError] = useState(false)
  const openAccountForAuthError = useCallback(() => setOpenAccountAfterAuthError(true), [])
  const auth = useAuthSession({ onAuthError: openAccountForAuthError })
  const identity = usePlayerIdentity(auth.activeUserId, auth.authResolved)
  const local = useLocalGame({
    activeUserId: auth.activeUserId,
    authResolved: auth.authResolved,
    ownerGeneration: auth.ownerGeneration,
    isCurrentOwner: auth.isCurrentOwner
  })
  const cloud = useCloudSave({
    api: apiClient,
    activeUserId: auth.activeUserId,
    authResolved: auth.authResolved,
    ownerGeneration: auth.ownerGeneration,
    localLoaded: local.localLoaded,
    saveOwner: local.saveOwner,
    localSave: local.save,
    localSaveUpdatedAt: local.saveUpdatedAt,
    guestSave: local.guestSave,
    guestSaveUpdatedAt: local.guestSaveUpdatedAt,
    isCurrentOwner: auth.isCurrentOwner,
    currentLocalGuard: local.currentLocalGuard,
    isCurrentLocalGuard: local.isCurrentLocalGuard,
    getLatestLocal: local.getLatestLocal,
    applyCloudDocument: local.applyCloudDocument,
    projectRevision: local.projectRevision,
    setProjectionReady: local.setProjectionReady,
    discardGuestSave: local.discardGuestSave,
    refreshAuth: auth.refreshAuth
  })

  const clearPaymentReturn = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete('payment_order')
    url.searchParams.delete('payment_provider')
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
    setPaymentReturnOrderId(null)
  }, [])

  useEffect(() => {
    if (!paymentReturnOrderId) return
    const url = new URL(window.location.href)
    const providerReturnFields = ['app_id', 'auth_app_id', 'charset', 'method', 'out_trade_no', 'seller_id', 'sign', 'sign_type', 'timestamp', 'total_amount', 'trade_no', 'version']
    let changed = false
    for (const field of providerReturnFields) {
      if (url.searchParams.has(field)) {
        url.searchParams.delete(field)
        changed = true
      }
    }
    if (changed) window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
  }, [paymentReturnOrderId])

  useEffect(() => {
    if (!paymentReturnOrderId || !auth.authResolved) return
    if (!auth.activeUserId) {
      identity.requireLogin('vip')
      return
    }
    if (identity.nickname && local.projectionReady) local.setPanel('vip')
  }, [auth.activeUserId, auth.authResolved, identity.nickname, local.projectionReady, local.setPanel, paymentReturnOrderId])

  const resumedGate = useRef('')
  useEffect(() => {
    if (!identity.gate) { resumedGate.current = ''; return }
    if (!auth.activeUserId || !identity.nickname || !identity.gate || !local.projectionReady) return
    const key = `${auth.activeUserId}:${identity.gate}`
    if (resumedGate.current === key) return
    resumedGate.current = key
    if (identity.gate === 'vip') local.setPanel('vip')
    else if (cloud.saveSelection) local.setPanel('account')
    else if (local.save?.runStarted) local.setScreen('game')
    identity.finishGate()
  }, [auth, identity, local, cloud])

  useEffect(() => {
    if (cloud.saveSelection && !identity.blocked) local.setPanel('account')
  }, [cloud.saveSelection, identity.blocked, local.setPanel])

  useViewportHeight()

  useEffect(() => {
    const onVisibility = () => setVisible(!document.hidden)
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  useEffect(() => {
    if (openAccountAfterAuthError) {
      local.setPanel('account')
      setOpenAccountAfterAuthError(false)
    }
  }, [local.setPanel, openAccountAfterAuthError])

  useEffect(() => {
    try {
      window.localStorage.setItem(PREF_KEY, JSON.stringify(preferences))
      window.localStorage.setItem('sun_snd', preferences.soundEnabled ? '1' : '0')
    } catch { /* optional guest preference */ }
  }, [preferences])

  const gameAudioActive = visible
  useEffect(() => {
    setGameAudioEnabled(preferences.soundEnabled)
    if (gameAudioActive) startGameBgm()
    else stopGameBgm()
  }, [gameAudioActive, preferences.soundEnabled])

  useEffect(() => {
    if (!preferences.soundEnabled) return
    const unlock = () => { if (!document.hidden) unlockGameAudio() }
    const events = ['pointerdown', 'pointerup', 'click', 'touchend', 'keydown', 'pageshow', 'focus'] as const
    events.forEach(event => window.addEventListener(event, unlock, true))
    document.addEventListener('visibilitychange', unlock)
    return () => {
      events.forEach(event => window.removeEventListener(event, unlock, true))
      document.removeEventListener('visibilitychange', unlock)
    }
  }, [preferences.soundEnabled])

  const visibleSave = auth.authResolved && local.saveOwner === auth.activeUserId ? local.save : null
  useEffect(() => {
    if (!auth.activeUserId && !identity.blocked && visibleSave?.phase === 'dead') identity.requestRevive()
  }, [auth.activeUserId, identity, visibleSave])

  const canContinuePlaying = () => !identity.blocked && (visibleSave?.phase !== 'dead' || identity.requestRevive())
  const onPanel = (panel: Panel) => {
    if (identity.blocked) return
    if (panel === 'vip' && !identity.requireLogin('vip')) { local.setPanel(null); return }
    local.setPanel(panel)
  }

  useEffect(() => {
    if (identity.blocked || !preferences.autoContinue || !visible || !visibleSave || visibleSave.phase !== 'result') return
    const timer = window.setTimeout(() => {
      if (!auth.isCurrentOwner(auth.ownerGeneration, auth.activeUserId)) return
      const current = local.getLatestLocal()
      if (current) local.commit(continueResult(current))
    }, preferences.autoDelay)
    return () => window.clearTimeout(timer)
  }, [auth.activeUserId, auth.isCurrentOwner, auth.ownerGeneration, local.commit, local.getLatestLocal, preferences.autoContinue, preferences.autoDelay, visible, visibleSave, identity.blocked])

  const onViewEnding = useCallback(() => {
    if (visibleSave?.phase === 'ended') {
      startGameBgm()
      local.setScreen('game')
    }
  }, [local.setScreen, visibleSave])

  const onHome = useCallback(() => {
    if (!canContinuePlaying()) return
    startGameBgm()
    local.clearBirthIntro()
    local.setScreen('home')
  }, [local.clearBirthIntro, local.setScreen, identity, visibleSave])

  const onStart = useCallback(() => {
    if (!canContinuePlaying()) return
    if (visibleSave?.runStarted && !identity.requireLogin('rebirth')) return
    startGameBgm()
    playGameSound('birth')
    local.start()
  }, [local.start, identity, visibleSave])

  const onContinue = useCallback(() => {
    if (!canContinuePlaying()) return
    startGameBgm()
    playGameSound('birth')
    local.continueRun()
  }, [local.continueRun, identity, visibleSave])

  const onToggleSound = useCallback(() => {
    const next = !preferences.soundEnabled
    setGameAudioEnabled(next)
    if (next) {
      startGameBgm()
      playGameSound('ding')
    }
    setPreferences(current => ({ ...current, soundEnabled: next }))
  }, [preferences.soundEnabled])

  const onGameChange = useCallback((next: GameState) => {
    if (identity.blocked) return
    const current = local.getLatestLocal()
    if (current && next.revives > current.revives) {
      if (!identity.requestRevive()) return
    }
    if (next.phase === 'dead' && current?.phase !== 'dead') playGameSound('death')
    else if (next.phase === 'fatePrompt' && current?.phase !== 'fatePrompt') playGameSound('fate')
    else if (next.phase === 'ended' && current?.phase !== 'ended') playGameSound('victory')
    else playGameSound('click')
    local.commit(next)
  }, [local.commit, local.getLatestLocal, identity])

  const dialogProps = {
    preferences,
    authSession: auth.authSession,
    authLoading: auth.authLoading,
    authError: auth.authError,
    cloudStatus: cloud.cloudStatus,
    cloudDocument: cloud.cloudDocument,
    saveSelection: cloud.saveSelection,
    locationLabel: auth.authResolved ? saveLocationLabel(auth.activeUserId) : '正在确认账号…',
    cloudError: cloud.cloudError,
    cloudActionError: cloud.cloudActionError,
    cloudActionBusy: cloud.cloudActionBusy,
    onClose: () => local.setPanel(null),
    onPreferences: (next: Preferences) => {
      // Keep audio activation inside the checkbox's trusted user gesture.
      setGameAudioEnabled(next.soundEnabled)
      if (next.soundEnabled) unlockGameAudio()
      setPreferences(next)
    },
    onReset: () => void local.resetLocal(),
    onLogin: auth.loginWithCasdoor,
    onLogout: () => void auth.logoutFromCasdoor(cloud.cloudStatus === 'pending' || cloud.cloudStatus === 'choosing'),
    onSync: cloud.syncCloudNow,
    onChooseGuest: () => void cloud.chooseGuestSave(),
    onChooseCloud: () => void cloud.chooseCloudSave(),
    onRefreshAuth: () => void auth.refreshAuth(),
    onResetCloud: (scope: 'run' | 'all') => void cloud.resetCloud(scope)
  }

  return (
    <PlayerAccessContext.Provider value={{ nickname: identity.nickname, avatarUrl: identity.avatarUrl, busy: identity.busy, error: identity.error, requestRevive: identity.requestRevive, submitName: identity.submitName, submitAvatar: identity.submitAvatar }}>
    <VipContext.Provider key={`${auth.activeUserId}:${auth.ownerGeneration}`} value={{ vipStatus: cloud.vipStatus, svipStatus: cloud.svipStatus, error: cloud.vipError, soundEnabled: preferences.soundEnabled, reduceMotion: preferences.reduceMotion || systemReduceMotion, initialTier: vipInitialTier, returnOrderId: paymentReturnOrderId, selectTier: setVipInitialTier, clearReturnOrder: clearPaymentReturn, refreshEntitlements: cloud.refreshEntitlements, beginPayment: cloud.beginVipPayment, getPayment: cloud.getVipPayment, confirmPayment: cloud.confirmVipPayment }}>
    <AppShell
      screen={local.screen}
      save={visibleSave}
      ready={auth.authResolved && local.projectionReady}
      saveStatus={local.saveStatus}
      birthIntroToken={local.birthIntroToken}
      restartMemories={local.restartMemories}
      onBirthIntroComplete={local.clearBirthIntro}
      locationLabel={auth.authResolved ? saveLocationLabel(auth.activeUserId) : '正在确认账号…'}
      reduceMotion={preferences.reduceMotion}
      soundEnabled={preferences.soundEnabled}
      panel={identity.blocked ? null : local.panel}
      dialogProps={dialogProps}
      onPanel={onPanel}
      onToggleSound={onToggleSound}
      onStart={onStart}
      onContinue={onContinue}
      onViewEnding={onViewEnding}
      onHome={onHome}
      onChange={onGameChange}
    />
    </VipContext.Provider>
    <PhoneLoginDialog open={auth.phoneLoginOpen} busy={auth.authLoading} onClose={auth.closePhoneLogin} onVerified={auth.completePhoneLogin} />
    {identity.blocked && <PlayerAccessDialog key={auth.activeUserId || 'guest'} loggedIn={Boolean(auth.activeUserId)} gate={identity.gate} busy={identity.busy} error={identity.error} authError={auth.authError} onLogin={auth.loginWithCasdoor} onSubmit={identity.submitName} onRetry={identity.retry} />}
    </PlayerAccessContext.Provider>
  )
}

export default App
